import { ReactNode } from 'react'
import { Button, Spinner } from '@/components/ds2'
import { BoxTruckLoadAnnotation, BoxTruckOperatorBid, LoadBid } from '@numeo/types'
import { CanonicalLoad } from '@numeo/types-core'
import { BoxTruckEmailThread } from '../hooks/useBoxTruckOverlays'
import { BoxTruckMarkAllRead } from './BoxTruckMarkAllRead'
import { BoxTruckTable, type BoxTruckColumnId } from './BoxTruckTable'
import { LoadingState, EmptyState, ErrorState, NewSearchState } from './BoxTruckBoardStates'
import type { BoxTruckPinnedBoard } from '../utils/pinnedBoard'

interface BoxTruckBoardProps {
    /** The active tab has run a search — false shows the "New search" hero (no request fired). */
    searched: boolean
    /** Which pinned board is on screen (Driver Bids / My Biddings), or null for a search tab. One prop
     *  rather than a boolean beside a label: two props would have to be kept in agreement, and the half
     *  that fell behind would describe the board the operator is NOT looking at. */
    pinnedBoard: BoxTruckPinnedBoard | null
    /** The ACTIVE pinned feed failed and has nothing to show. */
    pinnedFeedError: boolean
    onReloadPinnedFeed: () => void
    /** A fetch failed while rows were already on screen. The rows stay; the bottom edge says which attempt
     *  failed and offers the matching retry, because the alternative is a board that silently ends
     *  mid-scroll, or one that a pressed Refresh never updated. */
    failedFetch?: 'reload' | 'append' | null
    onRetryLoadMore?: () => void
    onRetryReload?: () => void
    error: string | null
    onRetry: () => void
    boardLoading: boolean
    /** Rows before the client text/hidden-source refinement — drives the loader/empty gate. */
    boardLoadsCount: number
    visibleLoads: CanonicalLoad[]
    loadingMore: boolean
    selectedKey: string | null
    onRowClick: (load: CanonicalLoad) => void
    /** Middle-click a row → open that load in a new browser tab (deep-linked). */
    onOpenNewTab: (load: CanonicalLoad) => void
    bids: Record<string, LoadBid[]>
    operatorBids: Record<string, BoxTruckOperatorBid[]>
    annotations: Record<string, BoxTruckLoadAnnotation>
    emailThreads?: Record<string, BoxTruckEmailThread>
    /** Loads whose driver bid this desk has not answered with a bid email — the Bids cell says so. */
    awaitingDesk?: ReadonlySet<string>
    onRate: (load: CanonicalLoad, rating: number) => void
    currentOperatorId?: string
    onEndReached: () => void
    /** Which columns the operator has chosen to see. */
    visibleColumns: Record<BoxTruckColumnId, boolean>
    /** User-selected display order for the loadboard columns. */
    columnOrder: BoxTruckColumnId[]
    /** Per-board virtual-table scroll state. Search tabs and Driver Bids must not share one scrollTop. */
    scrollKey: string
    readScrollTop: (key: string) => number
    writeScrollTop: (key: string, scrollTop: number) => void
    /** The column picker's trigger. Rendered directly above the table and only when there IS one — it has
     *  nothing to act on beside the empty state or the "New search" hero. */
    /** The table's settings opener — the one way into the column layout and the load-detail preferences. */
    settingsSlot?: ReactNode
}

/** The Box Truck board content area: the "New search" hero for an unsearched tab, else the error / loader /
 *  empty / virtualized-table states, with the load-more spinner pinned over the bottom edge. */
export const BoxTruckBoard = ({
    searched,
    pinnedBoard,
    pinnedFeedError,
    onReloadPinnedFeed,
    failedFetch,
    onRetryLoadMore,
    onRetryReload,
    error,
    onRetry,
    boardLoading,
    boardLoadsCount,
    visibleLoads,
    loadingMore,
    selectedKey,
    onRowClick,
    onOpenNewTab,
    bids,
    operatorBids,
    annotations,
    emailThreads,
    awaitingDesk,
    onRate,
    currentOperatorId,
    onEndReached,
    visibleColumns,
    columnOrder,
    scrollKey,
    readScrollTop,
    writeScrollTop,
    settingsSlot,
}: BoxTruckBoardProps) => {
    // Both pinned boards list loads that already carry a bid and run no lane query, so every branch that
    // only asks "is this a lane search" reads this rather than naming a board.
    const bidLoadsOnly = pinnedBoard !== null
    return (
        <div className="relative flex h-[65dvh] min-h-[420px] flex-none flex-col overflow-hidden min-[900px]:h-auto min-[900px]:min-h-0 min-[900px]:flex-1">
            {!bidLoadsOnly && !searched ? (
                // The active tab hasn't searched yet — show the "New search" hero, fire no request.
                <NewSearchState />
            ) : (
                <>
                    {bidLoadsOnly ? (
                        // A failed bid-loads fetch must NOT fall through to the "no bids yet" empty state — that
                        // would read a server error as a confirmed absence of bids.
                        pinnedFeedError ? (
                            <ErrorState
                                message={pinnedBoard === 'my_bids' ? 'Couldn’t load your bids' : 'Couldn’t load bid loads'}
                                onRetry={onReloadPinnedFeed}
                            />
                        ) : null
                    ) : error ? (
                        <ErrorState message={error} onRetry={onRetry} />
                    ) : null}
                    {(bidLoadsOnly ? !pinnedFeedError : !error) &&
                        // A fresh search clears the board first, so this shows the loader until the FIRST result
                        // streams in — then the virtualized table renders and the rest stream in on top.
                        (boardLoading && boardLoadsCount === 0 ? (
                            <LoadingState />
                        ) : visibleLoads.length === 0 ? (
                            <EmptyState hasLoads={boardLoadsCount > 0} pinnedBoard={pinnedBoard} />
                        ) : (
                            <>
                                <div className="flex shrink-0 items-center justify-end gap-3 px-3 pb-1">
                                    {/* Still fetching, but rows are already up — from a slower source, or from the
                                    lane of a second picked driver. The board must not go back to a blank
                                    loader for that; a quiet spinner over the table says "more is coming"
                                    while the operator reads what has arrived. */}
                                    {boardLoading && (
                                        <span className="mr-auto flex items-center gap-2 text-xs text-foreground-muted">
                                            <Spinner color="accent" size={14} />
                                            Loading more results…
                                        </span>
                                    )}
                                    <BoxTruckMarkAllRead loads={visibleLoads} />
                                    {settingsSlot}
                                </div>
                                {/* The virtualized table sizes itself to 100% of its parent, so it needs a box of
                                its own once something shares the column with it. */}
                                <div className="min-h-0 flex-1">
                                    <BoxTruckTable
                                        visibleColumns={visibleColumns}
                                        columnOrder={columnOrder}
                                        loads={visibleLoads}
                                        selectedKey={selectedKey}
                                        onRowClick={onRowClick}
                                        onOpenNewTab={onOpenNewTab}
                                        bids={bids}
                                        operatorBids={operatorBids}
                                        annotations={annotations}
                                        emailThreads={emailThreads}
                                        awaitingDesk={awaitingDesk}
                                        onRate={onRate}
                                        currentOperatorId={currentOperatorId}
                                        onEndReached={onEndReached}
                                        scrollKey={scrollKey}
                                        readScrollTop={readScrollTop}
                                        writeScrollTop={writeScrollTop}
                                    />
                                </div>
                            </>
                        ))}
                </>
            )}
            {loadingMore && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center border-t border-border bg-background/80 py-3">
                    <Spinner color="accent" size={20} />
                </div>
            )}
            {/* Not an error STATE: the rows stay and stay usable. This says which attempt failed and offers
                the matching retry. An append needs one because `endReached` has already fired for this
                scroll position, so scrolling cannot ask again; a reload needs one because otherwise a
                pressed Refresh does nothing visible and leaves a stale list reading as current. */}
            {!loadingMore && !boardLoading && failedFetch && (
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-3 border-t border-border bg-background/90 py-2 text-xs text-foreground-muted">
                    <span>{failedFetch === 'append' ? 'Couldn’t load more loads.' : 'Couldn’t refresh this list — showing the last loaded rows.'}</span>
                    {(failedFetch === 'append' ? onRetryLoadMore : onRetryReload) && (
                        <Button variant="outline" size="sm" onClick={failedFetch === 'append' ? onRetryLoadMore : onRetryReload}>
                            Try again
                        </Button>
                    )}
                </div>
            )}
        </div>
    )
}
